import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Loader2 } from "lucide-react";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = isLogin
      ? await login(form.email, form.password)
      : await register({ email: form.email, password: form.password, name: form.name, phone: form.phone });
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const dest = res.user.role === "admin" ? "/admin" : "/espace-client";
    navigate(location.state?.from || dest, { replace: true });
  };

  return (
    <div className="bg-[#FAFAFA] text-[#1A1918] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 md:pt-36 pb-24">
        <div className="max-w-md mx-auto px-6">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#C86641]">
            {isLogin ? "Connexion" : "Inscription"}
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] mt-4">
            {isLogin ? "Heureux de vous revoir." : "Créons votre espace."}
          </h1>
          <p className="mt-4 text-[#5C5A56]">
            {isLogin
              ? "Connectez-vous pour discuter en direct et suivre vos demandes de devis."
              : "Créez un compte pour discuter en direct et suivre vos projets."}
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid={isLogin ? "login-form" : "register-form"}>
            {!isLogin && (
              <>
                <FormField label="Nom complet">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                    data-testid="auth-name-input"
                  />
                </FormField>
                <FormField label="Téléphone (optionnel)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                    data-testid="auth-phone-input"
                  />
                </FormField>
              </>
            )}
            <FormField label="Email">
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                data-testid="auth-email-input"
              />
            </FormField>
            <FormField label="Mot de passe">
              <input
                required
                type="password"
                minLength={6}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full bg-[#F3F1ED] px-4 py-3.5 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
                data-testid="auth-password-input"
              />
            </FormField>

            {error && (
              <div data-testid="auth-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full bg-[#1A1918] text-[#FAFAFA] px-8 py-4 font-semibold hover:bg-[#C86641] transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {isLogin ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-[#5C5A56]">
            {isLogin ? (
              <>
                Pas encore de compte ?{" "}
                <Link to="/inscription" className="font-semibold text-[#1A1918] hover:text-[#C86641]" data-testid="auth-switch-register">
                  Inscrivez-vous
                </Link>
              </>
            ) : (
              <>
                Déjà inscrit ?{" "}
                <Link to="/connexion" className="font-semibold text-[#1A1918] hover:text-[#C86641]" data-testid="auth-switch-login">
                  Connectez-vous
                </Link>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C5A56] mb-2">{label}</div>
      {children}
    </label>
  );
}
